import { useAddBrandCost } from "@entities/brief/lib/hooks/useAddBrandCost";
import { useAddInitialBrandCost } from "@entities/brief/lib/hooks/useAddInitialBrandCost";
import { useEditBrandCost } from "@entities/brief/lib/hooks/useEditBrandCost";
import { AddCommercials } from "@features/InfluencerCommercialsV2/InfluencerCommercialsCost/AddCommercials";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { IBriefCampaignBriefInfluencer } from "@shared/lib/types/briefCampains";
import {
  IBrandCostRequest,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";

import { Button, Dropdown, Popover } from "@shared/ui";
import { Ellipsis } from "lucide-react";
import { useEffect, useState } from "react";

export default function BrandCommercialsDropdown({
  briefInfluencer,
  isHovered = false,
  onDropdownOpenChange,
}: {
  briefInfluencer: IBriefInfluencerV2 | IBriefCampaignBriefInfluencer;
  isHovered?: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}) {
  const { briefId } = useBriefStore();
  const { addInitialCost, isAdded, isAdding } = useAddInitialBrandCost(briefId);
  const {
    addCost,
    isAdded: isAddedCost,
    isAdding: isAddingCost,
  } = useAddBrandCost(briefId);

  const { editCost, isEditing, isEdited } = useEditBrandCost(briefId);

  const [openPopover, setOpenPopover] = useState<
    "edit" | "create-new" | "create" | ""
  >("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (onDropdownOpenChange) {
      onDropdownOpenChange(openPopover !== "" || dropdownOpen);
    }
  }, [openPopover, dropdownOpen, onDropdownOpenChange]);

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Initial cost added successfully");
      setOpenPopover("");
    }
    if (isAddedCost) {
      ToastService.success("Cost added successfully");
      setOpenPopover("");
    }
    if (isEdited) {
      ToastService.success("Cost updated successfully");
      setOpenPopover("");
    }
  }, [isAdded, isAddedCost, isEdited]);

  if (!briefInfluencer.brandCost) {
    return null;
  }

  return (
    <Popover
      open={openPopover !== ""}
      title={
        <span>
          {openPopover === "edit"
            ? "Edit Brand Commercial"
            : openPopover === "create-new"
              ? "Add New Brand Commercial"
              : "Add Brand Commercial"}
        </span>
      }
      trigger={["click"]}
      onOpenChange={(open) => {
        if (!open) setOpenPopover("");
      }}
      destroyTooltipOnHide
      content={
        openPopover !== "" ? (
          <AddCommercials
            initialValues={{
              cost: briefInfluencer.brandCost?.cost,
              note: briefInfluencer?.brandCost?.note,
            }}
            onClose={() => setOpenPopover("")}
            handleSave={(values) => {
              if (openPopover === "edit") {
                return editCost({
                  ...(values as Omit<IBrandCostRequest, "briefInfluencerId">),
                  isNonCancellable: (values as any)?.isNonCancellable || false,
                  briefInfluencerId: briefInfluencer._id,
                });
              } else if (openPopover === "create-new") {
                addCost({
                  ...(values as Omit<IBrandCostRequest, "briefInfluencerId">),
                  briefInfluencerId: briefInfluencer._id,
                  isNonCancellable: (values as any)?.isNonCancellable || false,
                });
              } else if (openPopover === "create") {
                addInitialCost({
                  ...(values as any),
                  briefInfluencerId: briefInfluencer._id,
                  isNonCancellable: (values as any)?.isNonCancellable || false,
                });
              }
            }}
            isLoading={isAdding || isAddingCost || isEditing}
            briefInfluencer={briefInfluencer}
            type="brand"
            isViewOnly={false}
          />
        ) : null
      }
    >
      <Dropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        menu={{
          items: [
            {
              key: "create-new",
              label: "Add New",
            },
            {
              key: "edit",
              label: "Edit",
            },
          ],
          onClick: ({ key, domEvent }) => {
            setOpenPopover(key as "edit" | "create-new" | "create");
            setDropdownOpen(false);
            domEvent.stopPropagation();
          },
        }}
      >
        {isHovered ? (
          <Button icon={<Ellipsis size={14} />} type="text" size="small" />
        ) : null}
      </Dropdown>
    </Popover>
  );
}
